import { useState, useCallback, useEffect, useRef } from 'react';
import { Head } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { PageHeader } from '../../components/PageHeader';
import { AddressCard } from '../../components/address/AddressCard';
import { AddressForm } from '../../components/address/AddressForm';
import { getCSRFToken } from '../../utils/csrf';
import type { SavedAddress } from '../../types/address';

interface AddressesProps {
  addresses: SavedAddress[];
  routes: {
    resolve: string;
    create: string;
    delete: string;
  };
  lastAddressId?: number | null;
}

export default function Addresses({ addresses: initialAddresses, routes, lastAddressId }: AddressesProps) {
  const [addresses, setAddresses] = useState<SavedAddress[]>(initialAddresses);
  const [newAddressId, setNewAddressId] = useState<number | null>(lastAddressId || null);
  const newCardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (newAddressId) {
      const timer = setTimeout(() => setNewAddressId(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [newAddressId]);

  useEffect(() => {
    if (newAddressId && newCardRef.current) {
      newCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [newAddressId]);

  const handleDelete = useCallback(async (id: number) => {
    const url = routes.delete.replace('__ID__', String(id));
    const response = await fetch(url, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'X-XSRF-TOKEN': getCSRFToken(),
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const result = await response.json();
    if (result.delete === 'ok') {
      setAddresses(prev => prev.filter(a => a.id !== id));
    } else {
      throw new Error(result.message || 'Error al eliminar la direccion');
    }
  }, [routes.delete]);

  const handleFormSuccess = useCallback(() => {
    window.location.reload();
  }, []);

  const hasAddresses = addresses.length > 0;

  return (
    <MainLayout>
      <Head>
        <title>Mis direcciones</title>
        <meta name="robots" content="noindex" />
      </Head>

      <div className="mx-auto max-w-6xl px-4 py-6">
        <PageHeader
          title="Mis direcciones"
          subtitle="Administra tus direcciones para recibir tus pedidos"
        />

        {/* Saved addresses */}
        {hasAddresses && (
          <section className="mb-10" aria-labelledby="saved-addresses-title">
            <h2 id="saved-addresses-title" className="mb-5 ml-1 text-lg font-semibold text-gray-800">
              Direcciones guardadas ({addresses.length})
            </h2>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 lg:gap-6">
              {addresses.map((address) => (
                <div
                  key={address.id}
                  ref={address.id === newAddressId ? newCardRef : undefined}
                >
                  <AddressCard address={address} onDelete={handleDelete} isNew={address.id === newAddressId} />
                </div>
              ))}

              {/* Add placeholder - desktop only */}
              <div className="hidden sm:block">
                <a
                  href="#add-address-form"
                  className="flex h-full min-h-[200px] flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-6 text-gray-500 no-underline transition-all hover:border-green-500 hover:bg-green-50 hover:text-green-500"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('add-address-form')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <svg className="mb-3 h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="text-base font-semibold">Agregar direccion</span>
                </a>
              </div>
            </div>
          </section>
        )}

        {/* Add address form */}
        <section
          id="add-address-form"
          className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:p-8"
          aria-labelledby="add-address-title"
        >
          <h2 id="add-address-title" className="mb-6 text-xl font-semibold text-gray-800 sm:text-[22px]">
            {hasAddresses ? 'Agregar nueva direccion' : 'Agrega tu primera direccion'}
          </h2>

          <AddressForm
            routes={{ resolve: routes.resolve, create: routes.create }}
            onSuccess={handleFormSuccess}
          />
        </section>

        {!hasAddresses && (
          <p className="mt-6 text-center text-sm text-gray-500">
            Agrega una direccion para poder realizar tu primer pedido
          </p>
        )}
      </div>
    </MainLayout>
  );
}
